import { types } from '@boss/boss-client';
import { Coordinates, sortByClosestLocation } from '@boss/utils';

type SortAfhaalpuntenOptions = {
  favouriteShopId?: string;
  coordinates?: Coordinates | null;
};

/**
 * The API may omit coordinates even though `types.Store` marks them required,
 * so guard on the runtime value instead of trusting the type.
 */
const hasUsableCoordinates = (store: types.Store): boolean =>
  typeof store.latitude === 'number' && typeof store.longitude === 'number';

/**
 * CL-1 cascade for the afhaalpunt list: the favourite is pinned first and the
 * rest is sorted by distance to the customer geolocation; without geolocation
 * by distance to the favourite; without any location in the incoming
 * channel-configured order. A favourite id that is not in the list (or has no
 * usable location) falls back to the corresponding non-favourite cascade.
 */
const sortAfhaalpunten = (
  stores: types.Store[],
  { favouriteShopId, coordinates }: SortAfhaalpuntenOptions,
): types.Store[] => {
  const favourite = favouriteShopId ? stores.find(store => store.id === favouriteShopId) : undefined;
  const remainingStores = favourite ? stores.filter(store => store.id !== favourite.id) : [...stores];

  if (!favourite) {
    return coordinates ? sortByClosestLocation(stores, coordinates) : [...stores];
  }

  if (coordinates) {
    return [favourite, ...sortByClosestLocation(remainingStores, coordinates)];
  }

  if (hasUsableCoordinates(favourite)) {
    return [
      favourite,
      ...sortByClosestLocation(remainingStores, { latitude: favourite.latitude, longitude: favourite.longitude }),
    ];
  }

  return [favourite, ...remainingStores];
};

export { sortAfhaalpunten };
